/** Home page: create a meeting, join by code, "my meetings". */

import { t, applyI18n, setScript, getScript } from './i18n';
import { $, $$, h, toast, copyText, openDialog } from './dom';
import { icons } from './icons';

const MINE_KEY = 'davra-meetings'; // [{ code, title, hostKey, createdAt }]

function loadMine() {
	try { return JSON.parse(localStorage.getItem(MINE_KEY)) || []; } catch { return []; }
}

function saveMine(list) {
	try { localStorage.setItem(MINE_KEY, JSON.stringify(list.slice(0, 30))); } catch { /* ignore */ }
}

/** "https://x/m/abc-defg-hij", "abcdefghij", "ABC-DEFG-HIJ" → "abc-defg-hij" */
export function parseCode(input) {
	const s = String(input || '').trim();
	const fromUrl = /\/m\/([a-z-]+)/i.exec(s);
	const letters = (fromUrl ? fromUrl[1] : s).toLowerCase().replace(/[^a-z]/g, '');
	if (letters.length !== 10) return null;
	return `${letters.slice(0, 3)}-${letters.slice(3, 7)}-${letters.slice(7)}`;
}

function renderMine() {
	const list = loadMine();
	$('#mine-empty').hidden = list.length > 0;
	$('#mine-list').replaceChildren(...list.map((m) => h('li', { class: 'mine-item' },
		h('a', { class: 'mine-link', href: `/m/${m.code}` },
			h('strong', {}, m.title || t('common.untitled')),
			h('span', { class: 'mono muted' }, m.code)),
		h('button', { class: 'icon-btn', title: t('top.copyLink'), html: icons.link, onclick: () => copyText(`${location.origin}/m/${m.code}`) }),
		h('button', {
			class: 'icon-btn', title: t('home.forget'), html: icons.close,
			onclick: () => { saveMine(loadMine().filter((x) => x.code !== m.code)); renderMine(); }
		}))));
}

async function createMeeting(options = {}) {
	const res = await fetch('/api/public/meetings', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(options)
	});
	const data = await res.json().catch(() => ({}));
	if (!res.ok) throw new Error((data.error && data.error.message) || t('home.error'));
	saveMine([{ code: data.code, title: data.title, hostKey: data.hostKey, createdAt: Date.now() }, ...loadMine().filter((m) => m.code !== data.code)]);
	return data;
}

function showCreated(meeting) {
	const url = meeting.url;
	openDialog({
		title: t('home.ready'),
		body: h('div', { class: 'created' },
			h('p', { class: 'muted' }, t('home.readyText')),
			h('div', { class: 'link-box' },
				h('span', { class: 'mono' }, url),
				h('button', { class: 'btn btn-sm', onclick: () => copyText(url) }, h('span', { class: 'btn-icon', html: icons.copy }), t('common.copy'))),
			meeting.passcode ? h('p', { class: 'small' }, `${t('info.passcode')}: `, h('strong', { class: 'mono' }, meeting.passcode)) : null),
		actions: [{ label: t('home.start'), kind: 'accent', run: () => { location.href = `/m/${meeting.code}`; } }]
	});
}

function setup() {
	$('#new-icon').innerHTML = icons.cam;
	$('#brand-mark').innerHTML = icons.logo;
	for (const el of $$('[data-icon]')) el.innerHTML = icons[el.dataset.icon];

	// Script switch
	const syncScript = () => {
		for (const b of $$('[data-script]')) b.classList.toggle('active', b.dataset.script === getScript());
	};
	for (const b of $$('[data-script]')) b.onclick = () => { setScript(b.dataset.script); syncScript(); renderMine(); };
	syncScript();
	applyI18n();
	renderMine();

	// Instant meeting
	$('#new-meeting').onclick = async (e) => {
		const btn = e.currentTarget;
		btn.disabled = true;
		try {
			const m = await createMeeting({});
			location.href = `/m/${m.code}`;
		} catch (err) {
			toast(err.message, { type: 'error' });
			btn.disabled = false;
		}
	};

	// Meeting with options
	$('#new-options').onclick = () => {
		const title = h('input', { class: 'input', maxLength: 120, placeholder: t('home.optTitlePh') });
		const pass = h('input', { class: 'input', maxLength: 40, autocomplete: 'off' });
		const waiting = h('input', { type: 'checkbox' });
		const mute = h('input', { type: 'checkbox' });
		const durLabel = (n) => (n % 60 === 0 ? t('home.hours', { n: n / 60 }) : n > 60 ? `${t('home.hours', { n: Math.floor(n / 60) })} ${t('home.minutes', { n: n % 60 })}` : t('home.minutes', { n }));
		const duration = h('select', { class: 'input' },
			h('option', { value: '0' }, t('home.noLimit')),
			...[15, 20, 30, 40, 45, 60, 80, 90, 120, 180].map((n) => h('option', { value: String(n) }, durLabel(n))),
			h('option', { value: 'custom' }, t('home.custom')));
		const custom = h('input', { class: 'input', type: 'number', min: 1, max: 1440, placeholder: t('home.customPh'), hidden: true });
		const hint = h('p', { class: 'small muted', hidden: true }, t('home.durationHint'));
		duration.onchange = () => {
			custom.hidden = duration.value !== 'custom';
			hint.hidden = duration.value === '0';
			if (!custom.hidden) custom.focus();
		};
		openDialog({
			title: t('home.newOptions'),
			body: h('div', { class: 'form' },
				h('label', { class: 'field' }, h('span', {}, t('home.optTitle')), title),
				h('label', { class: 'field' }, h('span', {}, t('home.optPass')), pass),
				h('label', { class: 'field' }, h('span', {}, t('home.optDuration')), duration, custom, hint),
				h('label', { class: 'check' }, waiting, h('span', {}, t('home.optWaiting'))),
				h('label', { class: 'check' }, mute, h('span', {}, t('home.optMute')))),
			actions: [{
				label: t('home.create'),
				kind: 'accent',
				run: async () => {
					try {
						const m = await createMeeting({
							title: title.value,
							passcode: pass.value,
							durationMinutes: duration.value === 'custom' ? Number(custom.value) || 0 : Number(duration.value),
							settings: { waitingRoom: waiting.checked, muteOnEntry: mute.checked }
						});
						renderMine();
						setTimeout(() => showCreated(m), 50);
					} catch (err) {
						toast(err.message, { type: 'error' });
						return false;
					}
					return true;
				}
			}]
		});
		setTimeout(() => title.focus(), 30);
	};

	// Join by code or link
	$('#join-form').onsubmit = (e) => {
		e.preventDefault();
		const code = parseCode($('#join-code').value);
		const err = $('#join-error');
		if (!code) {
			err.textContent = t('home.badCode');
			err.hidden = false;
			return;
		}
		location.href = `/m/${code}`;
	};
	$('#join-code').oninput = () => { $('#join-error').hidden = true; };
}

setup();
